/**
 * Central App Data Provider
 * 
 * Provides centralized data access for all core business data:
 * - Customers (from Firestore)
 * - Jobs (from Firestore)
 * - Events (from Firestore)
 * - Xero Invoices (from Firestore cache)
 * - Xero Repeating Invoices (from API)
 * - Customer Mappings (from Firestore)
 * 
 * All data is loaded on app startup with real-time Firestore listeners.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { collection, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { authFetch } from '../utils/cloudFunctionsAuth';
import {
  AppDataState,
  AppDataActions,
  AppDataContextType,
  NativePayment,
} from './types';
import { Customer } from '../pages/customers/CustomerInterfaces';
import { Job, CalendarEvent } from '../pages/job-summary/Interfaces';
import {
  XeroInvoice,
  XeroPayment,
  XeroRepeatingInvoice,
  CustomerMapping,
  XeroConnectionStatus,
  XeroRepeatingInvoicesResponse,
} from '../types/xero';

const FUNCTIONS_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

// ============================================
// Context
// ============================================

const AppDataContext = createContext<AppDataContextType | null>(null);

export function useAppData(): AppDataContextType {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
}

// ============================================
// Default State
// ============================================

const defaultState: AppDataState = {
  // Customers
  customers: [],
  customersLoading: true,
  customersError: null,
  
  // Jobs
  jobs: [],
  jobsLoading: true,
  jobsError: null,
  
  // Events
  events: [],
  eventsLoading: true,
  eventsError: null,
  
  // Xero
  xeroConnected: false,
  xeroConnectionStatus: null,
  invoices: [],
  bills: [],
  payments: [],
  repeatingInvoices: [],
  customerMappings: [],
  xeroLoading: true,
  xeroError: null,
  
  // Overall
  initialLoadComplete: false,
  initialLoadError: null,
};

// ============================================
// Provider
// ============================================

interface AppDataProviderProps {
  children: ReactNode;
}

export function AppDataProvider({ children }: AppDataProviderProps) {
  const [state, setState] = useState<AppDataState>(defaultState);

  // Helper to update state
  const updateState = useCallback((updates: Partial<AppDataState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // ============================================
  // Customers Listener
  // ============================================

  useEffect(() => {
    const customersQuery = query(
      collection(db, 'customers'),
      orderBy('name', 'asc')
    );
    
    const unsubscribe = onSnapshot(
      customersQuery,
      (snapshot) => {
        const customersData: Customer[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as Customer[];
        updateState({ 
          customers: customersData, 
          customersLoading: false, 
          customersError: null 
        });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch customers:', error);
        updateState({ 
          customersError: error.message, 
          customersLoading: false 
        });
      }
    );
    
    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Jobs Listener
  // ============================================

  useEffect(() => {
    const jobsQuery = query(
      collection(db, 'jobs'),
      orderBy('creationDate', 'desc')
    );
    
    const unsubscribe = onSnapshot(
      jobsQuery,
      (snapshot) => {
        const jobsData: Job[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as Job[];
        updateState({ 
          jobs: jobsData, 
          jobsLoading: false, 
          jobsError: null 
        });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch jobs:', error);
        updateState({ 
          jobsError: error.message, 
          jobsLoading: false 
        });
      }
    );
    
    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Events Listener
  // ============================================

  useEffect(() => {
    const eventsQuery = query(collection(db, 'events'));
    
    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const eventsData: CalendarEvent[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as CalendarEvent[];
        updateState({ 
          events: eventsData, 
          eventsLoading: false, 
          eventsError: null 
        });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch events:', error);
        updateState({ 
          eventsError: error.message, 
          eventsLoading: false 
        });
      }
    );
    
    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Native Invoices Listener (Firestore invoices collection)
  // ============================================

  useEffect(() => {
    const invoicesQuery = query(
      collection(db, 'invoices'),
      orderBy('dueDate', 'desc')
    );

    const unsubscribe = onSnapshot(
      invoicesQuery,
      (snapshot) => {
        const invoicesData: XeroInvoice[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            InvoiceID: doc.id,
            InvoiceNumber: data.invoiceNumber || doc.id,
            Reference: data.reference,
            Type: 'ACCREC',
            Status: data.status || 'DRAFT',
            Contact: {
              ContactID: data.contactId || '',
              Name: data.contactName || '',
            },
            DateString: data.date || '',
            Date: data.date,
            DueDateString: data.dueDate || '',
            DueDate: data.dueDate,
            Total: data.total || 0,
            AmountDue: data.amountDue ?? 0,
            AmountPaid: data.amountPaid ?? 0,
            CurrencyCode: data.currencyCode || 'AUD',
            SentToContact: data.sentToContact || false,
          } as XeroInvoice;
        });
        updateState({ invoices: invoicesData });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch invoices:', error);
      }
    );

    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Native Bills Listener (Firestore bills collection)
  // ============================================

  useEffect(() => {
    const billsQuery = query(
      collection(db, 'bills'),
      orderBy('dueDate', 'desc')
    );

    const unsubscribe = onSnapshot(
      billsQuery,
      (snapshot) => {
        const billsData: XeroInvoice[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            InvoiceID: doc.id,
            InvoiceNumber: data.billNumber || doc.id,
            Reference: data.reference,
            Type: data.type || 'ACCPAY',
            Status: data.status || 'DRAFT',
            Contact: {
              ContactID: data.contactId || '',
              Name: data.contactName || '',
            },
            DateString: data.date || '',
            Date: data.date,
            DueDateString: data.dueDate || '',
            DueDate: data.dueDate,
            Total: data.total || 0,
            AmountDue: data.amountDue ?? 0,
            AmountPaid: data.amountPaid ?? 0,
            CurrencyCode: data.currencyCode || 'AUD',
            SentToContact: false,
          } as XeroInvoice;
        });
        updateState({ bills: billsData });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch bills:', error);
      }
    );
    
    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Native Payments Listener (Firestore payments collection)
  // ------------------------------------------------------------------
  // Payments are the source of truth for WHEN cash moved. We attach them to the
  // matching invoice/bill below (as Payments[]) so the accounting charts can
  // count income/expense on a cash basis — the month a payment was made, not the
  // month the invoice was dated.
  // ============================================
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'payments'),
      (snapshot) => {
        const paymentsData: NativePayment[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            documentType: data.documentType === 'bill' ? 'bill' : 'invoice',
            documentId: data.documentId || '',
            amount: Number(data.amount) || 0,
            date: data.date || '',
          };
        });
        updateState({ payments: paymentsData });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch payments:', error);
      }
    );

    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Customer Mappings Listener
  // ============================================

  useEffect(() => {
    const mappingsQuery = query(collection(db, 'xero_customer_mappings'));
    
    const unsubscribe = onSnapshot(
      mappingsQuery,
      (snapshot) => {
        const mappingsData: CustomerMapping[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as CustomerMapping[];
        updateState({ customerMappings: mappingsData });
      },
      (error) => {
        console.error('[AppDataProvider] Failed to fetch customer mappings:', error);
      }
    );
    
    return () => unsubscribe();
  }, [updateState]);

  // ============================================
  // Xero Connection & Repeating Invoices
  // ============================================

  // Native accounting: no Xero connection check; data comes from Firestore listeners
  const fetchXeroData = useCallback(async () => {
    updateState({
      xeroLoading: false,
      xeroConnected: true,
      xeroConnectionStatus: { connected: true } as XeroConnectionStatus,
      xeroError: null,
      repeatingInvoices: [],
    });
  }, [updateState]);

  // Initial Xero data fetch
  useEffect(() => {
    fetchXeroData();
  }, [fetchXeroData]);

  // ============================================
  // Track Initial Load Complete
  // ============================================

  useEffect(() => {
    const allLoaded = 
      !state.customersLoading && 
      !state.jobsLoading && 
      !state.eventsLoading && 
      !state.xeroLoading;
    
    if (allLoaded && !state.initialLoadComplete) {
      const hasErrors = 
        state.customersError || 
        state.jobsError || 
        state.eventsError || 
        state.xeroError;
      
      updateState({ 
        initialLoadComplete: true,
        initialLoadError: hasErrors ? 'Some data failed to load' : null,
      });
      
      console.log('[AppDataProvider] Initial load complete', {
        customers: state.customers.length,
        jobs: state.jobs.length,
        events: state.events.length,
        invoices: state.invoices.length,
        repeatingInvoices: state.repeatingInvoices.length,
        customerMappings: state.customerMappings.length,
        xeroConnected: state.xeroConnected,
      });
    }
  }, [
    state.customersLoading,
    state.jobsLoading,
    state.eventsLoading,
    state.xeroLoading,
    state.customersError,
    state.jobsError,
    state.eventsError,
    state.xeroError,
    state.initialLoadComplete,
    state.customers.length,
    state.jobs.length,
    state.events.length,
    state.invoices.length,
    state.repeatingInvoices.length,
    state.customerMappings.length,
    state.xeroConnected,
    updateState,
  ]);

  // ============================================
  // Refresh Actions
  // ============================================

  const refreshCustomers = useCallback(() => {
    // Firestore onSnapshot handles this automatically
    // This is provided for API consistency
    updateState({ customersLoading: true });
    setTimeout(() => updateState({ customersLoading: false }), 100);
  }, [updateState]);

  const refreshJobs = useCallback(() => {
    updateState({ jobsLoading: true });
    setTimeout(() => updateState({ jobsLoading: false }), 100);
  }, [updateState]);

  const refreshEvents = useCallback(() => {
    updateState({ eventsLoading: true });
    setTimeout(() => updateState({ eventsLoading: false }), 100);
  }, [updateState]);

  const refreshXeroData = useCallback(async () => {
    await fetchXeroData();
  }, [fetchXeroData]);

  const refreshAll = useCallback(async () => {
    refreshCustomers();
    refreshJobs();
    refreshEvents();
    await refreshXeroData();
  }, [refreshCustomers, refreshJobs, refreshEvents, refreshXeroData]);

  // ============================================
  // Utility Functions
  // ============================================

  const getCustomerById = useCallback((id: string) => {
    return state.customers.find(c => c.id === id);
  }, [state.customers]);

  const getJobById = useCallback((id: string) => {
    return state.jobs.find(j => j.id === id);
  }, [state.jobs]);

  const getInvoiceById = useCallback((id: string) => {
    return state.invoices.find(i => i.InvoiceID === id);
  }, [state.invoices]);

  const getCustomerMappingByXeroContactId = useCallback((contactId: string) => {
    return state.customerMappings.find(m => m.xero_contact_id === contactId);
  }, [state.customerMappings]);

  const getCustomerMappingByCustomerId = useCallback((customerId: string) => {
    return state.customerMappings.find(m => m.customer_id === customerId);
  }, [state.customerMappings]);

  // ============================================
  // Attach payments to their invoices/bills (cash-basis enrichment)
  // ------------------------------------------------------------------
  // Build Payments[] (Xero shape) keyed by document, split by type so an invoice
  // id can never pick up a bill's payments. Invoices/bills that have real payment
  // records expose them as Payments[] (consumers then bucket by payment date);
  // those without keep the rolled-up amountPaid only (callers fall back to the
  // invoice date — best available for legacy/migrated docs with no payment rows).
  // ============================================
  const { invoicePaymentsByDoc, billPaymentsByDoc } = useMemo(() => {
    const inv = new Map<string, XeroPayment[]>();
    const bill = new Map<string, XeroPayment[]>();
    for (const p of state.payments) {
      if (!p.documentId || !p.date) continue;
      const target = p.documentType === 'bill' ? bill : inv;
      const arr = target.get(p.documentId) || [];
      arr.push({ PaymentID: p.id, Date: p.date, Amount: p.amount });
      target.set(p.documentId, arr);
    }
    return { invoicePaymentsByDoc: inv, billPaymentsByDoc: bill };
  }, [state.payments]);

  const enrichedInvoices = useMemo(
    () => state.invoices.map((inv) => {
      const pays = invoicePaymentsByDoc.get(inv.InvoiceID);
      return pays && pays.length ? { ...inv, Payments: pays } : inv;
    }),
    [state.invoices, invoicePaymentsByDoc]
  );

  const enrichedBills = useMemo(
    () => state.bills.map((bill) => {
      const pays = billPaymentsByDoc.get(bill.InvoiceID);
      return pays && pays.length ? { ...bill, Payments: pays } : bill;
    }),
    [state.bills, billPaymentsByDoc]
  );

  // ============================================
  // Context Value
  // ============================================

  const contextValue: AppDataContextType = useMemo(() => ({
    // State
    ...state,
    // Expose invoices/bills enriched with their payment records (overrides the
    // raw arrays spread from state above) so every consumer sees cash-basis data.
    invoices: enrichedInvoices,
    bills: enrichedBills,
    
    // Actions
    refreshCustomers,
    refreshJobs,
    refreshEvents,
    refreshXeroData,
    refreshAll,
    getCustomerById,
    getJobById,
    getInvoiceById,
    getCustomerMappingByXeroContactId,
    getCustomerMappingByCustomerId,
  }), [
    state,
    enrichedInvoices,
    enrichedBills,
    refreshCustomers,
    refreshJobs,
    refreshEvents,
    refreshXeroData,
    refreshAll,
    getCustomerById,
    getJobById,
    getInvoiceById,
    getCustomerMappingByXeroContactId,
    getCustomerMappingByCustomerId,
  ]);

  return (
    <AppDataContext.Provider value={contextValue}>
      {children}
    </AppDataContext.Provider>
  );
}

export default AppDataProvider;
